import { SBFormItemAddon } from '@components/SBFormItemAddon'
import {
  booleanSettingKeys,
  SettingKey,
  SettingKeyInputType,
  settingsByType,
} from '@models/Setting'
import { useAppSelector } from '@store/hooks'
import { selectSettingByKey } from '@store/SettingReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBRMFormAction, useSBRMForm } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { generateSelectOptions } from '@utils/generateSelectOptions'
import MetadataKey from '@utils/MetadataKey'
import { requiredRule, versionRule } from '@utils/rules'
import {
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
  Switch,
  TimePicker,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'
import TimeInputWithUnit from '../../event/TimeInputWithUnit'
import { StatusesFormInput } from '../StatusesFormInput'

const Update: SBRMFormAction = ({ form, formProps }) => {
  const { metadata, getMetadata } = useSBRMForm()

  const settingKey: SettingKey = useMemo(
    () => getMetadata(MetadataKey.Setting) as SettingKey,
    [metadata]
  )
  const extraProps: Record<string, any> | undefined = useMemo(
    () => getMetadata(MetadataKey.ExtraProps),
    [metadata]
  )

  const inputType = useMemo(() => {
    if (!settingKey) return null

    return settingsByType[settingKey] ?? null
  }, [settingKey])

  const item = useAppSelector(selectSettingByKey(settingKey))

  useEffect(() => {
    if (!settingKey) return

    if (booleanSettingKeys.includes(settingKey)) {
      form.setFieldsValue({
        id: item?.id,
        key: settingKey,
        value: item?.value == '1' ? true : false,
      })
      return
    } else if (
      settingKey === SettingKey.MEMBERSHIP_LOYALTY_TIER ||
      settingKey === SettingKey.BUSINESS_LOYALTY_TIER ||
      settingKey === SettingKey.MEMBERSHIP_CONFIRMATION_CONFIRMATION_STATUS
    ) {
      form.setFieldsValue({
        id: item?.id,
        key: settingKey,
        value: item && Number(item?.value),
      })
      return
    } else if (
      settingKey === SettingKey.MEMBERSHIP_BIRTHDAY_NOTIFICATION_TIME
    ) {
      form.setFieldsValue({
        id: item?.id,
        key: settingKey,
        value: item && JSON.parse(item?.value),
      })
      return
    }

    form.setFieldsValue({ id: item?.id, key: settingKey, value: item?.value })
  }, [item, settingKey])

  return (
    <Form {...formProps} form={form} layout="vertical">
      <Form.Item name="id" hidden noStyle>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="key" hidden noStyle>
        <Input type="hidden" />
      </Form.Item>
      {inputType &&
        {
          [SettingKeyInputType.inputText]: (
            <Form.Item
              name="value"
              rules={extraProps?.rules}
              label={extraProps?.label}
            >
              <Input />
            </Form.Item>
          ),
          [SettingKeyInputType.inputNumber]: (
            <Form.Item
              name="value"
              rules={extraProps?.rules}
              label={extraProps?.label}
            >
              <InputNumber
                {...extraProps}
                style={{ width: '100%', ...extraProps?.style }}
              />
            </Form.Item>
          ),
          [SettingKeyInputType.delay]: (
            <Form.Item name="value" rules={[]}>
              <TimeInputWithUnit name={'value'} value={item?.value} />
            </Form.Item>
          ),
          [SettingKeyInputType.delaySelect]: (
            <>
              <Form.Item name={['value', 'days']} label="Days" rules={[]}>
                <Select options={generateSelectOptions(-7, 2)} />
              </Form.Item>
              <Form.Item
                name={['value', 'hours']}
                label="Hours"
                rules={[]}
                getValueProps={(value) => ({
                  value: value ? dayjs(value, 'HH:mm') : undefined,
                })}
                getValueFromEvent={(time) => {
                  return time ? time.format('HH:mm') : null
                }}
              >
                <TimePicker format={'HH:mm'} allowClear={false} />
              </Form.Item>
            </>
          ),
          [SettingKeyInputType.tagEditor]: (
            <Form.Item name="value" rules={[]} label="Statuses">
              <StatusesFormInput fieldName="value" />
            </Form.Item>
          ),
          [SettingKeyInputType.appVersion]: (
            <SBFormItemAddon
              formItemProps={{
                name: 'value',
                rules: [versionRule()],
                label: 'Minimum version',
              }}
              addonBefore={'v'}
            >
              <Input />
            </SBFormItemAddon>
          ),
          [SettingKeyInputType.loyaltyTiers]: (
            <Form.Item
              name="value"
              rules={[requiredRule()]}
              label="Loyalty Tier"
            >
              <SBAsyncSelect entity={Entity.loyaltyTiers} allowClear />
            </Form.Item>
          ),
          [SettingKeyInputType.switch]: (
            <Form.Item
              name="value"
              rules={[]}
              label="Customer support chat is visible"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          ),
          [SettingKeyInputType.integrationSwitch]: (
            <Form.Item name="value" rules={[]} valuePropName="checked">
              <Switch />
            </Form.Item>
          ),
          [SettingKeyInputType.notificationSwitch]: (
            <Form.Item name="value" rules={[]} valuePropName="checked" noStyle>
              <Switch
                checkedChildren="Send"
                unCheckedChildren="Don't send"
                style={{ width: '100%' }}
              />
            </Form.Item>
          ),
          [SettingKeyInputType.notificationSegmented]: (
            <Form.Item name="value" rules={[]}>
              <Segmented
                block
                options={[
                  { value: true, label: 'Enabled' },
                  { value: false, label: 'Disabled' },
                ]}
              />
            </Form.Item>
          ),
          [SettingKeyInputType.membershipConfirmationStatus]: (
            <Form.Item name="value" rules={[]}>
              <SBAsyncSelect
                searchFieldName="name"
                entity={Entity.eventParticipationStatus}
              />
            </Form.Item>
          ),
        }[inputType]}
    </Form>
  )
}

export default Update
